/**
 * Achievement Card Component
 * 
 * Individual achievement display card
 */

import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Image, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { Achievement, AchievementId } from '@/services/achievements';
import { ACHIEVEMENT_DEFINITIONS } from '@/services/achievements';
import { getAchievementImage, hasAchievementImage } from './achievementImages';

// Try to import LinearGradient, fallback to View if not available
let LinearGradient: any = null;
try {
  const gradientModule = require('expo-linear-gradient');
  LinearGradient = gradientModule.LinearGradient;
} catch (e) {
  // LinearGradient not available, will use View fallback
  console.warn('expo-linear-gradient not available, using fallback');
}

type AchievementCardProps = {
  achievement: Achievement;
  progress?: number;
  showProgress?: boolean;
};

const TIER_COLORS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700',
  platinum: '#E5E4E2',
};

const TIER_GRADIENTS = {
  bronze: ['#CD7F32', '#B8732A', '#A06622'],
  silver: ['#C0C0C0', '#A8A8A8', '#909090'],
  gold: ['#FFD700', '#FFC700', '#FFB700'],
  platinum: ['#E5E4E2', '#D4D3D1', '#C3C2C0'],
};

// Apple Watch-style icon mapping for achievements
const ACHIEVEMENT_ICONS: Record<string, { icon: keyof typeof Ionicons.glyphMap; emoji?: string }> = {
  first_steps: { icon: 'footsteps', emoji: '👣' },
  country_collector_5: { icon: 'globe', emoji: '🌍' },
  country_collector_10: { icon: 'globe', emoji: '🌎' },
  country_collector_25: { icon: 'globe', emoji: '🌏' },
  country_collector_50: { icon: 'globe', emoji: '🗺️' },
  country_collector_100: { icon: 'globe', emoji: '🌐' },
  city_explorer_10: { icon: 'business', emoji: '🏙️' },
  city_explorer_25: { icon: 'business', emoji: '🏛️' },
  city_explorer_50: { icon: 'business', emoji: '🏗️' },
  city_explorer_100: { icon: 'business', emoji: '🌆' },
  continent_hopper: { icon: 'earth', emoji: '🌍' },
  weekend_warrior: { icon: 'calendar', emoji: '📅' },
  month_master_3: { icon: 'calendar', emoji: '📆' },
  month_master_6: { icon: 'calendar', emoji: '🗓️' },
  month_master_12: { icon: 'calendar', emoji: '📊' },
  landmark_hunter_10: { icon: 'location', emoji: '📍' },
  landmark_hunter_25: { icon: 'location', emoji: '🗺️' },
  landmark_hunter_50: { icon: 'location', emoji: '🗿' },
  world_wonder_seeker: { icon: 'star', emoji: '⭐' },
  icon_collector: { icon: 'camera', emoji: '📸' },
  solo_adventurer: { icon: 'person', emoji: '🧳' },
  family_explorer: { icon: 'people', emoji: '👨‍👩‍👧‍👦' },
  business_traveler: { icon: 'briefcase', emoji: '💼' },
  budget_master: { icon: 'wallet', emoji: '💰' },
  planning_pro: { icon: 'checkmark-circle', emoji: '✅' },
  days_abroad_100: { icon: 'calendar', emoji: '📅' },
  days_abroad_365: { icon: 'calendar', emoji: '🗓️' },
  miles_traveled_10000: { icon: 'airplane', emoji: '✈️' },
};

export function AchievementCard({
  achievement,
  progress,
  showProgress = true,
}: AchievementCardProps) {
  const theme = useTheme();
  const definition = ACHIEVEMENT_DEFINITIONS[achievement.id];
  const currentProgress = progress !== undefined ? progress : achievement.progress;
  const progressPercentage = Math.min((currentProgress / achievement.target) * 100, 100);
  const isUnlocked = achievement.unlocked;

  const iconInfo = ACHIEVEMENT_ICONS[achievement.id] || { icon: definition.icon as keyof typeof Ionicons.glyphMap };
  const iconName = iconInfo.icon;
  const tierColor = achievement.tier ? TIER_COLORS[achievement.tier] : theme.colors.accent;
  const tierGradient = achievement.tier ? TIER_GRADIENTS[achievement.tier] : [theme.colors.accent, theme.colors.primary];
  const unlockedMetaTextColor = theme.colors.text;

  // Image handling
  const hasImage = hasAchievementImage(achievement.id);
  const imageUrl = hasImage ? getAchievementImage(achievement.id, isUnlocked) : null;
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const useImage = hasImage && imageUrl && !imageError;

  // Reset image state when achievement changes
  useEffect(() => {
    if (hasImage && imageUrl) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [achievement.id, imageUrl, hasImage]);

  return (
    <Card
      variant={isUnlocked ? 'elevated' : 'outlined'}
      elevation={isUnlocked ? 3 : 0}
      padding={0}
      style={[
        styles.card,
        !isUnlocked && { opacity: 0.65 },
        isUnlocked && styles.unlockedCard,
      ]}
    >
      {/* Apple Watch-style gradient background for unlocked achievements */}
      {isUnlocked && achievement.tier && (
        LinearGradient ? (
          <LinearGradient
            colors={tierGradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.gradientBackground}
            opacity={0.1}
          />
        ) : (
          <View style={[styles.gradientBackground, { backgroundColor: tierColor + '10' }]} />
        )
      )}
      
      <View style={styles.contentWrapper}>
        <View style={styles.header}>
          <View style={styles.iconSection}>
            {isUnlocked && achievement.tier ? (
              LinearGradient ? (
                <LinearGradient
                  colors={tierGradient}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.iconContainerGradient}
                >
                  <View style={styles.iconInner}>
                    {useImage ? (
                      <View style={styles.imageContainer}>
                        {imageLoading && (
                          <ActivityIndicator
                            size="small"
                            color="#FFFFFF"
                            style={styles.imageLoader}
                          />
                        )}
                        <Image
                          source={{ uri: imageUrl! }}
                          style={[
                            styles.achievementImage,
                            imageLoading && styles.imageLoading,
                          ]}
                          onLoad={() => setImageLoading(false)}
                          onError={() => {
                            setImageError(true);
                            setImageLoading(false);
                          }}
                          resizeMode="cover"
                        />
                      </View>
                    ) : iconInfo.emoji ? (
                      <View style={styles.emojiContainer}>
                        <Body style={styles.emoji}>{iconInfo.emoji}</Body>
                      </View>
                    ) : (
                      <Ionicons
                        name={iconName}
                        size={48}
                        color="#FFFFFF"
                      />
                    )}
                  </View>
                  {isUnlocked && (
                    <View style={[styles.checkmark, { backgroundColor: '#FFFFFF' }]}>
                      <Ionicons name="checkmark" size={14} color={tierColor} />
                    </View>
                  )}
                </LinearGradient>
              ) : (
                <View style={[styles.iconContainerGradient, { backgroundColor: tierColor }]}>
                  <View style={styles.iconInner}>
                    {useImage ? (
                      <View style={styles.imageContainer}>
                        {imageLoading && (
                          <ActivityIndicator
                            size="small"
                            color="#FFFFFF"
                            style={styles.imageLoader}
                          />
                        )}
                        <Image
                          source={{ uri: imageUrl! }}
                          style={[
                            styles.achievementImage,
                            imageLoading && styles.imageLoading,
                          ]}
                          onLoad={() => setImageLoading(false)}
                          onError={() => {
                            setImageError(true);
                            setImageLoading(false);
                          }}
                          resizeMode="cover"
                        />
                      </View>
                    ) : iconInfo.emoji ? (
                      <View style={styles.emojiContainer}>
                        <Body style={styles.emoji}>{iconInfo.emoji}</Body>
                      </View>
                    ) : (
                      <Ionicons
                        name={iconName}
                        size={48}
                        color="#FFFFFF"
                      />
                    )}
                  </View>
                  {isUnlocked && (
                    <View style={[styles.checkmark, { backgroundColor: '#FFFFFF' }]}>
                      <Ionicons name="checkmark" size={14} color={tierColor} />
                    </View>
                  )}
                </View>
              )
            ) : (
              <View
                style={[
                  styles.iconContainer,
                  {
                    backgroundColor: theme.colors.background,
                    borderWidth: 2,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                {useImage ? (
                  <View style={styles.imageContainer}>
                    {imageLoading && (
                      <ActivityIndicator
                        size="small"
                        color={theme.colors.textTertiary}
                        style={styles.imageLoader}
                      />
                    )}
                    <Image
                      source={{ uri: imageUrl! }}
                      style={[
                        styles.achievementImage,
                        styles.achievementImageLocked,
                        imageLoading && styles.imageLoading,
                      ]}
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageError(true);
                        setImageLoading(false);
                      }}
                      resizeMode="cover"
                    />
                  </View>
                ) : iconInfo.emoji ? (
                  <Body style={[styles.emoji, { fontSize: 32, opacity: 0.5 }]}>
                    {iconInfo.emoji}
                  </Body>
                ) : (
                  <Ionicons
                    name={iconName}
                    size={32}
                    color={theme.colors.textTertiary}
                  />
                )}
              </View>
            )}
          </View>
          {achievement.tier && (
            <View
              style={[
                styles.tierBadge,
                isUnlocked && { backgroundColor: tierColor + '30' },
                !isUnlocked && { backgroundColor: theme.colors.background, borderWidth: 1, borderColor: theme.colors.border },
              ]}
            >
              <Caption
                style={[
                  styles.tierText,
                  { color: isUnlocked ? tierColor : theme.colors.textTertiary, textTransform: 'uppercase' },
                ]}
              >
                {achievement.tier}
              </Caption>
            </View>
          )}
        </View>

        <View style={styles.content}>
          <Heading style={[styles.name, isUnlocked && { color: theme.colors.text }]}>
            {definition.name}
          </Heading>
          <Body style={[styles.description, { color: theme.colors.textSecondary }]}>
            {definition.description}
          </Body>

          {showProgress && (
            <View style={styles.progressContainer}>
              <View style={[styles.progressBar, !isUnlocked && { backgroundColor: theme.colors.background }]}>
                {isUnlocked && achievement.tier && LinearGradient ? (
                  <LinearGradient
                    colors={tierGradient}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[
                      styles.progressFill,
                      { width: `${progressPercentage}%` },
                    ]}
                  />
                ) : (
                  <View
                    style={[
                      styles.progressFill,
                      {
                        width: `${progressPercentage}%`,
                        backgroundColor: isUnlocked && achievement.tier ? tierColor : theme.colors.accent,
                      },
                    ]}
                  />
                )}
              </View>
              <View style={styles.progressTextContainer}>
                <Caption
                  style={[
                    styles.progressText,
                    { color: theme.colors.textTertiary },
                  ]}
                >
                  {currentProgress} / {achievement.target}
                </Caption>
                <Caption
                  style={[
                    styles.progressPercentage,
                    { color: theme.colors.textTertiary },
                  ]}
                >
                  {Math.round(progressPercentage)}%
                </Caption>
              </View>
            </View>
          )}

          {isUnlocked && achievement.unlockedAt && (
            <View
              style={[
                styles.unlockedBadge,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                },
              ]}
            >
              <Ionicons name="trophy" size={12} color={tierColor} />
              <Caption style={[styles.unlockedText, { color: unlockedMetaTextColor }]}>
                Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
              </Caption>
            </View>
          )}
        </View>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    minWidth: 170,
    maxWidth: 200,
    overflow: 'hidden',
  },
  unlockedCard: {
    borderWidth: 2,
    borderColor: 'transparent',
  },
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  contentWrapper: {
    padding: 16,
    position: 'relative',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  iconSection: {
    alignItems: 'center',
  },
  iconContainerGradient: {
    width: 100, // Increased from 80
    height: 100, // Increased from 80
    borderRadius: 50, // Increased from 40
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  iconInner: {
    width: 88, // Increased from 72
    height: 88, // Increased from 72
    borderRadius: 44, // Increased from 36
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  emojiContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  emoji: {
    fontSize: 44, // Increased from 36
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  achievementImage: {
    width: '100%',
    height: '100%',
    borderRadius: 44, // Match iconInner borderRadius
  },
  achievementImageLocked: {
    opacity: 0.5,
  },
  imageLoader: {
    position: 'absolute',
  },
  imageLoading: {
    opacity: 0.3,
  },
  checkmark: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  tierBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  tierText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  content: {
    flex: 1,
  },
  name: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 6,
    letterSpacing: -0.3,
  },
  description: {
    fontSize: 13,
    marginBottom: 14,
    lineHeight: 18,
  },
  progressContainer: {
    marginTop: 10,
  },
  progressBar: {
    height: 12, // Increased from 6
    backgroundColor: '#E0E0E0',
    borderRadius: 6, // Increased from 3
    overflow: 'hidden',
    marginBottom: 6,
  },
  progressFill: {
    height: '100%',
    borderRadius: 6, // Increased from 3
  },
  progressTextContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressText: {
    fontSize: 11,
    fontWeight: '600',
  },
  progressPercentage: {
    fontSize: 11,
    fontWeight: '700',
  },
  unlockedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    gap: 4,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignSelf: 'flex-start',
  },
  unlockedText: {
    fontSize: 11,
    fontWeight: '800',
  },
});

